import React, { useState, useEffect, useRef } from 'react';
import "../components/styles/CustomProductSecondPage.css";
import {useNavigate} from "react-router-dom";
import { z } from "zod";
import {useLoggedInUserId} from "../api/GetLoggedInUserId";
import { toast } from "../utils/toast";
import { trackFormStart, trackLead, trackContactClick } from "../utils/analytics";

// embedded: 소개 페이지에 끼워 넣을 때 자체 페이지 헤더를 숨긴다.
const CustomProductSecondPage = ({ embedded = false }) => {
    let navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [formData, setFormData] = useState({
        name: '', email: '', phoneNumber: '', title: '', content: '', fileNames: ''
    });
    const [tempArticleId, setTempArticleId] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const formStarted = useRef(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);  // 로그인 여부 확인

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const MAX_FILE_COUNT = 5;

    // ✅ 로그인된 사용자 ID 가져오기
    const { data: memberId, isLoading, isError } = useLoggedInUserId();

    // ✅ 유효성 검사 스키마 (Zod)
    // 필드가 하나 늘 때마다 작성 완료율이 떨어진다.
    // 필수는 이름·연락처(이메일 또는 전화)·내용 3개. 제목은 서버가 내용에서 만든다.
    const schema = z
        .object({
            name: z.string().min(2, "이름은 2~10자 이내여야 합니다.").max(10, "이름은 2~10자 이내여야 합니다."),
            email: z.string().email("올바른 이메일 형식이어야 합니다.").or(z.literal("")),
            phoneNumber: z
                .string()
                .regex(/^010\d{8}$/, "전화번호는 010으로 시작하는 11자리 숫자여야 합니다.")
                .or(z.literal("")),
            content: z.string().min(10, "내용은 10~1000자 이내여야 합니다.").max(1000, "내용은 10~1000자 이내여야 합니다."),
        })
        .refine((d) => Boolean(d.email) || Boolean(d.phoneNumber), {
            path: ["email"],
            message: "이메일 또는 전화번호 중 하나는 입력해주세요.",
        });

    // ✅ 기존 임시저장된 데이터 불러오기
    useEffect(() => {
        const fetchDraft = async () => {
            try {
                // 로그인 되어있다면 임시저장 버튼 활성화
                if (memberId) {
                    setIsAuthenticated(true);
                }

                const response = await fetch('/api/apply/custom-product');
                if (response.ok) {
                    const data = await response.json();
                    setFormData({
                        name: data.name || '',
                        email: data.email || '',
                        phoneNumber: data.phoneNumber || '',
                        title: data.title || '',
                        content: data.content || '',
                    });
                    setTempArticleId(data.applicationArticleId);
                    // 임시저장에 첨부가 없으면 fileNames 가 아예 없거나 빈 문자열로 온다.
                    // 그대로 split 하면 터지거나(undefined) 빈 파일 한 개가 생긴다("").
                    setFiles(
                        typeof data.fileNames === "string" && data.fileNames.trim()
                            ? data.fileNames.split(",").map((f) => f.trim()).filter(Boolean)
                            : []
                    );
                }
            } catch (error) {
                console.error("임시저장 데이터 불러오기 실패:", error);
            }
        };
        fetchDraft();
    }, []);

    // const handleInputChange = (e) => {
    //     const { name, value } = e.target;
    //
    //     setFormData((prev) => ({ ...prev, [name]: value }));
    // };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        let totalFiles = files.length + selectedFiles.length;

        if (totalFiles > MAX_FILE_COUNT) {
            toast("파일은 최대 5개까지만 업로드할 수 있습니다.");
            return;
        }

        const validFiles = selectedFiles.filter((file) => {
            if (file.size > MAX_FILE_SIZE) {
                toast(`${file.name}은(는) 10MB를 초과하였습니다.`);
                return false;
            }
            return true;
        });

        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    };

    // 제출 중에는 화면을 덮어 진행 중임을 보이고, 같은 신청이 두 번 들어가는 것도 막는다.
    // 서버가 메일까지 보내고 응답하던 동안 아무 반응이 없어 "보내진 건가" 싶었다.
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    // 화면 표시는 state 로, 중복 차단은 ref 로 한다. state 는 다음 렌더에 반영돼서
    // 엔터와 클릭이 연달아 들어오는 경우를 막지 못한다.
    const submittingRef = useRef(false);

    // ✅ 맞춤상품 신청 제출 기능
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return;

        // const data = new FormData();
        // data.append('name', formData.name);
        // data.append('email', formData.email);
        // data.append('phoneNumber', formData.phoneNumber);
        // data.append('title', formData.title);
        // data.append('content', formData.content);

        // files.forEach((file) => {
        //     data.append('files', file);
        // });

        if (!privacyAgreed) {
            setErrors({ privacy: "개인정보 수집·이용에 동의해주세요." });
            toast("개인정보 수집·이용에 동의해주세요.");
            return;
        }

        // parse는 실패 시 예외를 던진다. 잡지 않으면 핸들러가 조용히 죽어
        // 사용자에게 아무 반응도 보이지 않는다.
        const parsed = schema.safeParse(formData);
        if (!parsed.success) {
            const fieldErrors = {};
            parsed.error.issues.forEach((issue) => {
                const key = issue.path[0] || "form";
                if (!fieldErrors[key]) fieldErrors[key] = issue.message;
            });
            setErrors(fieldErrors);
            toast(parsed.error.issues[0]?.message || "입력 내용을 확인해주세요.");
            return;
        }
        setErrors({});

        // 제목은 보내지 않는다. 서버가 내용 첫 줄로 채운다.
        const data = {
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            content: formData.content,
            fileNames: files.join(",")
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            const response = await fetch('/api/apply/custom-product', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                trackLead({
                    hasFile: files.length > 0,
                    hasEmail: Boolean(formData.email),
                    hasPhone: Boolean(formData.phoneNumber),
                });
                toast('신청이 접수되었습니다. 영업일 1~2일 내로 연락드리겠습니다.');
                setFormData({ name: '', email: '', phoneNumber: '', title: '', content: '', fileNames: '' });
                setFiles([]);
                setUploadedFiles([]);
                setPrivacyAgreed(false);
                if (!embedded) navigate("/custom-product-first");
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                toast('신청 제출 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('신청 제출 중 오류:', error);
            toast('서버 오류');
        } finally {
            // 성공·실패 어느 쪽이든 버튼을 되살린다. 여기서 빠뜨리면 영영 못 누른다.
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleSaveDraft = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.content || (!formData.email && !formData.phoneNumber)) {
            toast("필수 항목을 모두 입력해주세요.");
            return;
        }

        const draftParsed = schema.safeParse(formData);
        if (!draftParsed.success) {
            toast(draftParsed.error.issues[0]?.message || "입력 내용을 확인해주세요.");
            return;
        }
        setErrors({});

        // ✅ JSON 데이터로 변환
        const requestData = {
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            title: formData.title,
            content: formData.content,
            fileNames: files.join(","), // ✅ JSON 요청에는 파일명을 문자열 리스트로 전달
        };

        console.log(requestData);

        try {
            const response = await fetch("/api/apply-form/temporary-store", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestData),
            });

            const result = await response.json();

            if (result.message === "임시저장이 완료되었습니다.") {
                toast("임시저장 완료!");
            } else if (result.message === "로그인이 필요합니다.") {
                toast("로그인 후 이용해주세요.");
            } else {
                toast("임시저장 실패");
            }
        } catch (error) {
            console.error("임시저장 중 오류 발생:", error);
            toast("서버 오류");
        }
    };


    // ✅ 임시저장 삭제
    const handleDeleteDraft = async () => {
        if (!tempArticleId) {
            toast("삭제할 임시저장 내역이 없습니다.");
            return;
        }

        try {
            const response = await fetch(`/api/application/temp/${tempArticleId}`, {
                method: 'DELETE',
            });

            const result = await response.json();

            if (result.result === 'success') {
                toast("임시저장 삭제 완료!");
                setFormData({ name: '', email: '', phoneNumber: '', title: '', content: '' });
                setTempArticleId(null);
            } else {
                toast("임시저장 삭제 실패");
            }
        } catch (error) {
            console.error("임시저장 삭제 중 오류:", error);
            toast("서버 오류");
        }
    };

    const MAX_FILES = 5;
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB

    // 파일별로 순차 업로드한다. 서버가 요청당 파일 하나를 돌려주므로
    // 한 번에 몰아 보내면 응답과 목록이 어긋난다.
    const uploadFiles = async (fileList) => {
        const picked = Array.from(fileList || []);
        if (!picked.length) return;

        if (files.length + picked.length > MAX_FILES) {
            toast(`파일은 최대 ${MAX_FILES}개까지 첨부할 수 있습니다. (현재 ${files.length}개)`);
            return;
        }

        const tooBig = picked.find((f) => f.size > MAX_SIZE);
        if (tooBig) {
            toast(`"${tooBig.name}" 파일이 10MB를 초과합니다.`);
            return;
        }

        setIsUploading(true);
        try {
            for (const file of picked) {
                const data = new FormData();
                data.append("file", file);

                const response = await fetch("/api/upload-file", {
                    method: "POST",
                    body: data,
                });
                const result = await response.json();

                if (result.result === "success") {
                    setUploadedFiles((prev) => [...prev, result.d]); // storeFileName 저장
                    setFiles((prev) => [...prev, result.filename]);
                } else {
                    toast(`"${file.name}" 업로드에 실패했습니다.`);
                }
            }
        } catch (error) {
            console.error("파일 업로드 오류:", error);
            toast("서버 오류");
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileUpload = async (event) => {
        await uploadFiles(event.target.files);
        event.target.value = ""; // 같은 파일 재선택이 가능하도록 초기화
    };

    const handleFileDrop = async (event) => {
        event.preventDefault();
        setIsDragOver(false);
        await uploadFiles(event.dataTransfer.files);
    };

    const handleFileDelete = async (index) => {
        // 해당 파일 이름 가져오기
        let fileName = uploadedFiles[index];

        if (!fileName) {
            toast("임시 저장된 파일은 삭제가 불가능합니다.");
            return;
        }

        try {
            const response = await fetch(`/api/delete-file?fileId=${fileName}`, {
                method: "GET",
            });

            const result = await response.json();

            if (result.result === "success") {
                setUploadedFiles((prev) => prev.filter((f) => f !== fileName));
                setFiles((prev) => prev.filter((f, i) => i !== index));

                // input value 초기화

                toast("파일 삭제 성공!");
            } else {
                toast("파일 삭제 실패");
            }
        } catch (error) {
            console.error("파일 삭제 오류:", error);
            toast("서버 오류");
        }
    };

    // ✅ 입력 변경 핸들러
    const handleInputChange = (e) => {
        if (!formStarted.current) {
            formStarted.current = true;
            trackFormStart();
        }
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    // ✅ 실시간 입력 검증
    const validateField = (name, value) => {
        try {
            schema.pick({ [name]: schema.shape[name] }).parse({ [name]: value });
            setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
        } catch (error) {
            if (error instanceof z.ZodError) {
                setErrors((prevErrors) => ({ ...prevErrors, [name]: error.errors[0].message }));
            }
        }
    };

    // ✅ 예시 파일 다운로드
    const handleExampleDownload = async () => {
        try {
            const res = await fetch("/api/download-example-file");
            if (!res.ok) {
                toast("❌ 예시 파일 다운로드 실패");
                return;
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "소프트웨어_요구사항_명세서_SRS.docx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast("✅ 예시 파일 다운로드 완료!");
        } catch (error) {
            console.error("다운로드 중 오류 발생:", error);
            toast("서버 오류로 다운로드에 실패했습니다.");
        }
    };



    return (
        <>
            {!embedded && (
                <div className="sch-ph">
                    <div className="sch-wrap">
                        <div className="sch-no">CONTACT — 맞춤 개발</div>
                        <h1>맞춤 개발 문의</h1>
                        <p>
                            상담 신청이 완료되면 영업일 1~2일 내로 연락드립니다.
                            법에 저촉되는 프로그램 문의는 받지 않습니다.
                        </p>
                    </div>
                </div>
            )}

            <div className="sch-wrap sch-cgrid" style={{ borderBottom: 0 }}>
                <form className="sch-cform" onSubmit={handleSubmit}>
                    {isSubmitting && (
                        <div className="sch-sending" role="status" aria-live="polite">
                            <span className="sch-spinner sch-spinner--lg" aria-hidden="true" />
                            <strong>신청을 보내는 중입니다</strong>
                            <span>창을 닫지 말고 잠시만 기다려 주세요.</span>
                        </div>
                    )}
                    <label className={`sc-field${errors.name ? " sc-field--error" : ""}`}>
                        <span className="sc-field__label">이름<span className="sc-req">*</span></span>
                        <input
                            type="text"
                            name="name"
                            className="sc-input"
                            placeholder="이름을 입력해 주세요"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.name && <span className="sc-field__error">{errors.name}</span>}
                    </label>

                    <div className="sc-field__help" style={{ marginTop: "-4px" }}>
                        연락처는 이메일·전화번호 중 <b>하나만</b> 적어주셔도 됩니다.
                    </div>

                    <div className="sch-fgrid2">
                        <label className={`sc-field${errors.email ? " sc-field--error" : ""}`}>
                            <span className="sc-field__label">이메일</span>
                            <input
                                type="email"
                                name="email"
                                className="sc-input"
                                placeholder="이메일을 입력해 주세요"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                            {errors.email && <span className="sc-field__error">{errors.email}</span>}
                        </label>

                        <label className={`sc-field${errors.phoneNumber ? " sc-field--error" : ""}`}>
                            <span className="sc-field__label">전화번호</span>
                            <input
                                type="text"
                                name="phoneNumber"
                                className="sc-input"
                                placeholder="'-' 없이 숫자만 입력"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                inputMode="numeric"
                            />
                            {errors.phoneNumber && <span className="sc-field__error">{errors.phoneNumber}</span>}
                        </label>
                    </div>

                    <label className={`sc-field${errors.content ? " sc-field--error" : ""}`}>
                        <span className="sc-field__label">설명<span className="sc-req">*</span></span>
                        <textarea
                            name="content"
                            className="sc-textarea"
                            style={{ minHeight: "140px" }}
                            placeholder="자동화하고 싶은 업무를 자유롭게 써주세요 (10자 이상)"
                            value={formData.content}
                            onChange={handleInputChange}
                            maxLength={1000}
                            required
                        />
                        <span style={{ display: "flex", justifyContent: "space-between" }}>
                            <span className="sc-field__help">
                                {errors.content
                                    ? <span className="sc-field__error">{errors.content}</span>
                                    : "자동화하려는 업무와 현재 처리 방식을 함께 적어주시면 상담이 빠릅니다"}
                            </span>
                            <span className="sc-field__help">{formData.content.length}/1000</span>
                        </span>
                    </label>

                    <div className="sc-field">
                        <span className="sc-field__label">참고 파일</span>
                        <div className="sc-file">
                            <div
                                className={`sc-file__drop${isDragOver ? " sc-file__drop--over" : ""}${files.length >= MAX_FILES ? " sc-file__drop--full" : ""}`}
                                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                                onDragLeave={() => setIsDragOver(false)}
                                onDrop={handleFileDrop}
                            >
                                <input
                                    type="file"
                                    multiple
                                    aria-label="파일 첨부"
                                    disabled={isUploading || files.length >= MAX_FILES}
                                    onChange={handleFileUpload}
                                />
                                <span className="sc-file__icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 16V4M7 9l5-5 5 5" />
                                        <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                    </svg>
                                </span>
                                <span className="sc-file__main">
                                    {isUploading
                                        ? "업로드 중…"
                                        : <>파일을 끌어다 놓거나 <em>클릭해서 선택</em></>}
                                </span>
                                <span className="sc-file__hint">파일당 최대 10MB, 최대 {MAX_FILES}개까지 첨부 가능</span>
                            </div>

                            {files.length > 0 && (
                                <ul className="sc-file__list">
                                    {files.map((file, index) => (
                                        <li key={index} className="sc-file__item">
                                            <span className="sc-file__name">{file}</span>
                                            <button
                                                type="button"
                                                className="sc-file__x"
                                                aria-label={`${file} 삭제`}
                                                onClick={() => handleFileDelete(index)}
                                            >
                                                ✕
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="sc-file__foot">
                                <span className="sc-field__help">
                                    <button type="button" className="sch-inlinelink" onClick={handleExampleDownload}>
                                        요구사항 명세서 예시 내려받기 ↓
                                    </button>
                                </span>
                                <span className="sc-field__help">{files.length}/{MAX_FILES}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`sc-field${errors.privacy ? " sc-field--error" : ""}`}>
                        <label className="sc-choice">
                            <input
                                type="checkbox"
                                className="sc-check"
                                checked={privacyAgreed}
                                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                            />
                            <span>
                                개인정보 수집·이용에 동의합니다<span className="sc-req">*</span>
                            </span>
                        </label>
                        <span className="sc-field__help">
                            수집 항목: 이름, 이메일·전화번호, 문의 내용 · 목적: 상담 신청 처리 및 회신 ·
                            보유 기간: 상담 종료 후 1년 ·{" "}
                            <button
                                type="button"
                                className="sch-inlinelink"
                                onClick={() => window.open("/privacy-policy", "_blank", "noopener")}
                            >
                                전문 보기
                            </button>
                        </span>
                        {errors.privacy && <span className="sc-field__error">{errors.privacy}</span>}
                    </div>

                    <div className="sch-cbtns">
                        <button type="submit" className="sch-bigbtn" disabled={isSubmitting}
                                style={{ justifyContent: "center", flex: 1 }}>
                            {isSubmitting ? (
                                <>
                                    <span className="sch-spinner" aria-hidden="true" />
                                    보내는 중…
                                </>
                            ) : (
                                <>
                                    신청하기
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M13 6l6 6-6 6" />
                                    </svg>
                                </>
                            )}
                        </button>
                        {isAuthenticated && (
                            <button type="button" className="sc-btn sc-btn--secondary sc-btn--lg"
                                    onClick={handleSaveDraft} disabled={isSubmitting}>
                                임시저장
                            </button>
                        )}
                    </div>

                    <p className="sch-mono" style={{ margin: "2px 0 0" }}>
                        제출된 상담 내역은 마이페이지 → 상담신청내역에서 확인할 수 있습니다.
                    </p>

                    <div className="sc-accordion" style={{ marginTop: "6px" }}>
                        <details>
                            <summary>맞춤상품 상담 신청시 유의사항</summary>
                            <div className="sc-accordion__body">
                                <ul style={{ margin: 0, paddingLeft: "18px" }}>
                                    <li>상담 신청이 완료되면 영업일 1~2일 내로 연락드리오니, 수신에 유의해 주세요.</li>
                                    <li>본 신청서는 원활한 상담을 위한 초기 단계입니다. 세부 사항은 추후 상담에서 전달해 주세요.</li>
                                    <li>임시저장 또는 제출된 내역은 마이페이지 &gt; 상담신청내역에서 볼 수 있습니다.</li>
                                    <li>로그인하지 않은 상태로 신청한 내역은 다시 볼 수 없습니다. 수정·재확인이 필요하면 고객센터를 이용해 주세요.</li>
                                    <li>법에 저촉되는 프로그램 문의는 일체 받지 않습니다.</li>
                                    <li>신청 후에는 조회만 가능하며 수정이 불가능합니다.</li>
                                </ul>
                            </div>
                        </details>
                    </div>
                </form>

                <div className="sch-cside">
                    <div className="sch-chan">
                        <span className="sch-t">KAKAO — 월–금(공휴일 제외) 10:00–22:00</span>
                        <b>카카오톡 채널 상담</b>
                        <button
                            type="button"
                            className="sch-kakao"
                            onClick={() => { trackContactClick("kakao"); window.open("http://pf.kakao.com/_xkVeGn/chat", "_blank", "noopener"); }}
                        >
                            💬 채널에서 문의하기
                        </button>
                    </div>

                    <div className="sch-chan">
                        <span className="sch-t">EMAIL</span>
                        <b>oracle7579@gmail.com</b>
                        <a href="mailto:oracle7579@gmail.com" onClick={() => trackContactClick("email")}>메일 보내기 ↗</a>
                    </div>

                    <div className="sch-chan" style={{ borderBottom: 0 }}>
                        <span className="sch-t">TEL — 오전 09시~오후 18시</span>
                        <b>010-4414-7579</b>
                        <a href="tel:01044147579" onClick={() => trackContactClick("tel")}>전화 걸기 ↗</a>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomProductSecondPage;
